import React from 'react';

// Export the helper to get styles
export const provablyFairUiStyles = (): ProvablyFairUIStyles =>
  getProvablyFairUIStyles();

// Define the Interface
export interface ProvablyFairUIStyles {
  overlay: React.CSSProperties;

  modal: React.CSSProperties;
  modalTablet: React.CSSProperties;
  modalMobile: React.CSSProperties;

  header: React.CSSProperties;
  headerScrollActive?: React.CSSProperties;
  headerContent: React.CSSProperties;
  title: React.CSSProperties;
  headerBust: React.CSSProperties;
  headerTime: React.CSSProperties;
  closeButton: React.CSSProperties;
  closeButtonHover?: React.CSSProperties;
  closeIcon?: React.CSSProperties;

  contentArea: React.CSSProperties;
  contentInner?: React.CSSProperties;

  // Info Sections
  infoSection: React.CSSProperties;
  infoSectionHeader: React.CSSProperties;
  infoSectionImage: React.CSSProperties;
  infoTextGroup: React.CSSProperties;
  infoTitle: React.CSSProperties;
  infoSubTitle: React.CSSProperties;
  infoValueBox: React.CSSProperties;

  // Lists & Player Rows
  clientSeedList: React.CSSProperties;
  playerRow: React.CSSProperties;
  playerRowMobile: React.CSSProperties;
  playerInfo: React.CSSProperties;
  playerNameLabel: React.CSSProperties;
  playerAvatar: React.CSSProperties;
  playerAvatarImage: React.CSSProperties;
  playerId: React.CSSProperties;
  seedContainer: React.CSSProperties;
  seedLabel: React.CSSProperties;
  playerSeed: React.CSSProperties;

  // Results
  resultSection: React.CSSProperties;
  resultGrid: React.CSSProperties;
  resultGridMobile: React.CSSProperties;
  resultBox: React.CSSProperties;
  resultLabel: React.CSSProperties;
  resultValue: React.CSSProperties;

  // Footer
  modalFooter: React.CSSProperties;
  modalFooterLink: React.CSSProperties;
}

// implementation
export const getProvablyFairUIStyles = (): ProvablyFairUIStyles => ({
  // overlay covers the screen and lets internal modal scroll
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'flex-start', // So modal appears from top
    justifyContent: 'center',
    zIndex: 2000,
    padding: 8, // Padding is now handled by individual modal margins
    pointerEvents: 'auto',
    overflowY: 'auto', // Allow overlay to scroll
  },

  // --- MODAL LAYOUTS ---
  modal: {
    background: '#1b1c1d',
    borderRadius: 12,
    width: '100%',
    maxWidth: 750,
    // Desktop can just be auto height usually, or constrained if needed
    // maxHeight: '90vh', // Removed to allow outer scroll
    color: '#FFFFFF',
    fontFamily: "'Inter', sans-serif",
    pointerEvents: 'auto',
    userSelect: 'text',
    // Give desktop view some breathing room from viewport edges
    margin: '16px auto',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
  },

  // kept for completeness but option A uses modalMobile for widths < 1024
  modalTablet: {
    background: '#1b1c1d',
    borderRadius: 12,
    width: '100%',
    maxWidth: 700,
    // Vital for landscape phones detected as tablets:
    // maxHeight: '90vh', // Removed to allow outer scroll
    color: '#FFFFFF',
    fontFamily: "'Inter', sans-serif",
    // On tablets, remove vertical margin to have it flush with the top
    margin: '0 auto',
    position: 'relative',
    pointerEvents: 'auto',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },

  // MOBILE modal (used for phones, mobile landscape and tablets per Option A)
  modalMobile: {
    background: '#1b1c1d',
    borderRadius: 17,
    width: '100%',
    maxWidth: '100%',
    // This ensures the modal fits within the viewport, leaving space for header/footer
    // maxHeight: 'calc(100% - 10px)', // Removed to allow outer scroll
    color: '#FFFFFF',
    fontFamily: "'Inter', sans-serif",
    margin: 0,
    position: 'relative',
    pointerEvents: 'auto',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Ensures header/footer clip correctly
  },

  // --- HEADER ---
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0px',
    backgroundColor: '#2c2d30',
    // No border radius here, handled by parent overflow: hidden
    flexShrink: 0, // Prevents header from shrinking
    fontWeight: 400,
    fontSize: 16,
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    lineHeight: '16px',
    color: '#fff',
    // minHeight: '50px', // Ensure touch target size
  },

  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    fontFamily: 'Inter, sans-serif',
  },

  title: {
    fontSize: 14,
    fontWeight: 400,
    color: '#fff',
    textTransform: 'uppercase' as React.CSSProperties['textTransform'],
    letterSpacing: '0.6px',
    margin: 0,
  },

  headerBust: {
    fontSize: 14,
    fontWeight: 600,
    padding: '4px 4px',
    borderRadius: 10,
    backgroundColor: '#00000080',
  },

  headerTime: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: 400,
  },

  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0px',
    // display: 'flex',
    // alignItems: 'center',
    // justifyContent: 'center',
    width: 30,
    height: 30,
    boxShadow: 'none',
    outline: 'none',
    color: '#fff',
    fontSize: 20,
  },

  // --- CONTENT ---
  // contentArea uses internal scroll when screen is small so header/footer stay visible
  contentArea: {
    padding: '12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    flexGrow: 1,
    // Scrolling is now handled by the overlay.
    // height: '100%', // Removed
    // overflowY: 'auto', // Removed
  },
  contentInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  infoSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flexShrink: 0, // Prevent section from getting squashed
  },

  infoSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    paddingLeft: 4,
  },

  infoSectionImage: {
    width: 32,
    height: 32,
    objectFit: 'contain' as React.CSSProperties['objectFit'],
    opacity: 0.95,
  },

  infoTextGroup: {
    display: 'flex',
    flexDirection: 'column',
    color: '#fff',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '14px',
  },

  infoTitle: {
    fontSize: 14,
    fontWeight: 500,
    color: '#fff',
    lineHeight: '14px',
  },

  infoSubTitle: {
    fontSize: 13,
    color: '#ffffff80',
    marginTop: 3,
    lineHeight: '14px',
    fontWeight: 400,
    fontFamily: 'Inter, sans-serif',
  },

  // value boxes (hashes, seeds, server seed)
  infoValueBox: {
    background: 'rgba(7,7,8,0.32)',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 13,
    color: '#fff',
    wordBreak: 'break-word' as React.CSSProperties['wordBreak'],
    whiteSpace: 'nowrap' as React.CSSProperties['whiteSpace'],
    overflowX: 'auto',
  },

  // --- PLAYERS LIST ---
  clientSeedList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '6px 0',
    background: 'transparent',

    borderRadius: 8,
  },

  // desktop row (for >=1024) — kept but will only apply on desktop
  playerRow: {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    background: 'rgba(7,7,8,0.32)',
    borderRadius: 8,
    padding: '10px',
    gap: 8,
    whiteSpace: 'nowrap',
    overflowX: 'auto',
  },

  // MOBILE row - stacked column, used for phones/tablets/landscape (Option A)
  playerRowMobile: {
    display: 'flex',
    flexDirection: 'row',
    background: '#0d0e10',
    justifyContent: 'space-evenly',
    borderRadius: 8,
    padding: '10px',
    gap: 8,
  },

  playerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },

  playerNameLabel: {
    fontSize: 13,
    color: '#ffffff80',
    fontWeight: 500,
    lineHeight: '14px',
    marginRight: 6,
  },

  // Aviator uses slightly smaller avatar (28px) so layout is compact
  playerAvatar: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    overflow: 'hidden',
    border: '2px solid rgba(58,58,58,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  playerAvatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as React.CSSProperties['objectFit'],
  },

  playerId: {
    fontSize: 14,
    fontWeight: 700,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'Inter, sans-serif',
  },

  seedContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'flex-end',
    marginTop: 4,
    whiteSpace: 'nowrap',
    overflowX: 'auto',
  },

  seedLabel: {
    fontSize: 13,
    color: '#ffffff80',
    fontWeight: 400,
    fontFamily: 'Inter, sans-serif',
  },

  playerSeed: {
    fontSize: 12,
    color: '#fff',
    whiteSpace: 'nowrap',
    overflowX: 'auto',
    maxWidth: 160,
  },

  // --- RESULTS ---
  resultSection: {
    marginTop: 6,
  },

  resultGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
  },

  resultGridMobile: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 12,
  },

  resultBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },

  resultLabel: {
    fontSize: 14,
    color: '#ffffff80',
    marginBottom: 3,
    fontWeight: 400,
    textTransform: 'uppercase' as React.CSSProperties['textTransform'],
    fontFamily: 'Inter, sans-serif',
    lineHeight: '14px',
  },

  resultValue: {
    background: 'rgba(7,7,8,0.32)',
    borderRadius: 8,
    padding: '10px 12px',
    color: '#fff',
    fontSize: 14,
    fontWeight: 400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: 40,
    textAlign: 'center' as React.CSSProperties['textAlign'],
    wordBreak: 'break-all' as React.CSSProperties['wordBreak'],
    fontFamily: 'Inter, sans-serif',
  },

  // --- FOOTER ---
  modalFooter: {
    padding: '12px',
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 400,
    color: '#ffffffb3',
    background: '#2c2d30',
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    marginTop: 'auto',
  },

  modalFooterLink: {
    color: '#e30c32',
    textDecoration: 'none',
    marginLeft: 6,
    cursor: 'pointer',
    fontWeight: 400,
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
  },
});
